/**
 * Screen — VALUATION DASHBOARD (`/valuation`).
 *
 * VAL-ACTOR-JOURNEY-1-SIGNER R1: the engagement list is the front door;
 * opening a new engagement is its own route (`/valuation/new`,
 * ValuationProfileSelect). Fix 3b: the "Next step" column and the "Open"
 * link both come from the shared `nextStep(engagement, role)` (./_nextStep)
 * — slice A's per-row heuristic is gone.
 *
 * VAL-HISTORY-2: only in-progress (non-signed_off) engagements show here —
 * signed ones live in History (/valuation/history).
 *
 * VAL-DASHBOARD-NEXT-1: the page is "what needs me next" — three count tiles,
 * up to 5 open rows in the server's open_first order (oldest last-edited
 * first), icon-only action chips. The table stays in History only.
 */
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { listValuationEngagements } from '@/api/valuation'
import { formatApiError } from '@/utils/httpErrors'
import { useAuth } from '@/contexts/AuthContext'
import type { ValuationEngagement } from '@/types/valuation'
import { nextStep } from './_nextStep'
import { stepRole } from './_steps'
import { ActionChip } from './_ActionChip'
import { RunDemoButton } from './_RunDemoButton'
import { centralDateLabel, daysLabel, daysSince } from './_dashboardDates'
import { useEffect, useState } from 'react'
import { tileStyle } from '@/utils/palette'

const MAX_ROWS = 5
/** UI-PALETTE-1: `tone` picks the palette tile tone; label and value take its text colour. */
function Tile({ label, value, tone }: { label: string; value: number; tone: number }) {
  return (
    <div className="rounded-lg border p-4" style={tileStyle(tone)}>
      <div className="text-xs font-medium uppercase tracking-wide">{label}</div>
      <div className="mt-1 text-3xl font-bold">{value}</div>
    </div>
  )
}

/** First name only. The auth layer falls back to the email when there is no
 *  name, so anything with an '@' counts as no name — never greet by email. */
function firstNameOf(name: string | undefined): string | null {
  const first = (name ?? '').trim().split(/\s+/)[0]
  return first && !first.includes('@') ? first : null
}

export default function ValuationDashboard() {
  const { user } = useAuth()
  const first = firstNameOf(user?.name)
  const [engagements, setEngagements] = useState<ValuationEngagement[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    listValuationEngagements({ openFirst: true })
      .then(res => { if (live) setEngagements(res) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  const role = user?.role
  // The server returns open engagements first, oldest last-edited first.
  const inProgress = engagements.filter(en => en.status !== 'signed_off')
  const now = new Date()
  const readyToSign = inProgress.filter(en => nextStep(en, role).kind === 'sign').length
  // Only Finalize stamps valuation_method, so a set method is a recorded estimate.
  const recordedRecently = engagements.filter(
    en => !!en.valuation_method && daysSince(en.updated_at, now) <= 30,
  ).length
  const signedRecently = engagements.filter(
    en => en.status === 'signed_off' && daysSince(en.updated_at, now) <= 30,
  ).length
  const shown = inProgress.slice(0, MAX_ROWS)

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {loadError}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      {engagements.length > 0 && (
        <h1 className="text-2xl font-bold text-gray-900">
          {first ? `Hello, ${first}. Welcome back.` : 'Hello. Welcome back.'}
        </h1>
      )}

      {engagements.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="max-w-md text-sm text-gray-900">
              {first ? `Welcome, ${first}. ` : 'Welcome. '}
              Let's value your first business. Upload a profit and loss statement, answer a few questions, and you'll see a value range.
            </p>
            <Link to="/valuation/new" className="inline-block">
              <Button type="button">Start a valuation</Button>
            </Link>
          </CardContent>
        </Card>
      ) : inProgress.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm font-medium text-gray-900">
              Nothing in progress. Signed engagements are in History.
            </p>
            <Link to="/valuation/history" className="inline-block">
              <Button type="button" variant="outline">Go to History</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Tile label="Open" value={inProgress.length} tone={0} />
            {/* POL-B: the owner's label is owner-approved (OWNER_APPROVALS.log 2026-10-22 21:28 CT (a)). */}
            <Tile label={stepRole(role) === 'owner' ? 'Ready for your report' : 'Ready to sign'} value={readyToSign} tone={1} />
            {/* WALKFIX-B F14 (WF-B-3): an owner's estimate is never signed — count recorded estimates. */}
            {stepRole(role) === 'owner'
              ? <Tile label="Estimates recorded, last 30 days" value={recordedRecently} tone={2} />
              : <Tile label="Signed, last 30 days" value={signedRecently} tone={2} />}
          </div>

          <h2 className="text-lg font-semibold text-gray-900">Open, waiting longest first</h2>
          <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
            {shown.map((en, i) => {
              const ns = nextStep(en, role)
              return (
                <li key={en.valuation_engagement_id} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">{en.subject_company_name}</div>
                    <div className="text-xs text-gray-500">
                      {centralDateLabel(en.updated_at)} · {daysLabel(daysSince(en.updated_at, now))}
                    </div>
                  </div>
                  <ActionChip label={ns.label} route={ns.route} kind={ns.kind} raised={i === 0} />
                </li>
              )
            })}
          </ul>
          <p className="text-sm text-gray-600">
            Showing {shown.length} of {inProgress.length} open.{' '}
            <Link to="/valuation/history" className="font-medium text-blue-700 underline">See all in History</Link>
          </p>

          <Link to="/valuation/new" className="inline-block">
            <Button type="button">Start a new valuation</Button>
          </Link>
        </>
      )}

      <RunDemoButton />
    </div>
  )
}
